import { Add as AddIcon, Person as PersonIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { DeleteModal } from "../components/delete-dialog.tsx";
import { PageHeader } from "../components/page-header";
import PersonaCard from "../components/persona-card.tsx";
import PersonaDialog from "../components/persona-dialog.tsx";
import { SearchBar } from "../components/search-bar.tsx";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useDebounce } from "../hooks/use-debounce.ts";
import {
  useCreatePersona,
  useDeletePersona,
  usePersonas,
  useUpdatePersona,
} from "../hooks/use-personas.ts";
import { useUrlModal } from "../hooks/use-url-dialog";
import { STATUS_COLORS } from "../theme/theme";
import {
  type ApiConfig,
  DBStatus,
  type Persona,
  type PersonaCreateSchema,
  type PersonaUpdateSchema,
} from "../types.ts";

export default function PersonasPage() {
  const { personaId: paramPersonaId } = useParams();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAppConfig, setSelectedAppConfig] = useState<string | undefined>();

  // URL-based modal state (fixes back button behavior)
  const [dialogOpen, setDialogOpen] = useUrlModal("edit");

  const [editPersona, setEditPersona] = useState<Persona | null>(null);
  const [viewPersona, setViewPersona] = useState<Persona | null>(null);
  const [deletePersona, setDeletePersona] = useState<Persona | null>(null);

  // Sample persona for demonstration when no personas exist
  const samplePersona: Persona = {
    id: "sample",
    name: "Example Customer Support",
    description:
      "A friendly and knowledgeable customer support representative who helps users with product inquiries, order issues, and general assistance.",
    system_prompt:
      "You are a helpful customer support agent for an e-commerce platform. You are friendly, patient, and knowledgeable about products and policies. Always prioritize customer satisfaction while following company guidelines. Respond to customer inquiries with empathy and provide accurate information about products, orders, returns, and policies.",
    user: "support@example.com",
    password: "securepassword123",
    organization_id: "sample-org",
    created_at: new Date().toISOString(),
    created_by: "sample-user",
    status: DBStatus.ACTIVE,
  };

  const { enqueueSnackbar } = useSnackbar();
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch app configurations
  const { data: apiConfig, isLoading: isLoadingConfig } = useApiQuery<ApiConfig>("/config");

  // Fetch personas with filtering using the new hook
  const { data: personas, isLoading: isLoadingPersonas } = usePersonas({
    q: debouncedSearchQuery,
    app_config_id: selectedAppConfig?.replace("#", "") || undefined,
  });

  useEffect(() => {
    // If a personaId is provided in the URL, find and set it as the edit persona
    if (paramPersonaId && personas) {
      const foundPersona = personas.find((persona) => persona.id === paramPersonaId);
      if (foundPersona) {
        setEditPersona(foundPersona);
        setDialogOpen(true);
      } else {
        enqueueSnackbar("Persona not found", { variant: "error" });
      }
    } else {
      setEditPersona(null);
    }
  }, [paramPersonaId, personas, enqueueSnackbar, setDialogOpen]);

  // Mutations using the new hooks
  const { mutate: createPersona, isPending: isCreating } = useCreatePersona({
    onSuccess: () => {
      setDialogOpen(false);
    },
  });

  const { mutate: updatePersona, isPending: isUpdating } = useUpdatePersona({
    onSuccess: () => {
      setDialogOpen(false);
      setEditPersona(null);
    },
  });

  const { mutate: deletePersonaMutation, isPending: isDeleting } = useDeletePersona({
    onSuccess: () => {
      setDeletePersona(null);
    },
  });

  const filteredPersonas = useMemo(() => {
    if (!personas) return [];
    return personas;
  }, [personas]);

  const handleCreate = () => {
    setEditPersona(null);
    setDialogOpen(true);
  };

  const handleEdit = (persona: Persona) => {
    setEditPersona(persona);
    setDialogOpen(true);
  };

  const handleDelete = (persona: Persona) => {
    setDeletePersona(persona);
  };

  const handleConfirmDelete = () => {
    if (deletePersona) {
      deletePersonaMutation({ _url: `/personas/${deletePersona.id}` });
    }
  };

  const handleView = (persona: Persona) => {
    setViewPersona(persona);
  };

  const handleCopy = (persona: Persona) => {
    navigator.clipboard.writeText(persona.system_prompt);
    enqueueSnackbar("System prompt copied to clipboard", {
      variant: "success",
    });
  };

  const handleSubmit = (data: PersonaCreateSchema | PersonaUpdateSchema) => {
    if (editPersona) {
      updatePersona({ ...data, _url: `/personas/${editPersona.id}` });
    } else {
      createPersona(data as PersonaCreateSchema);
    }
  };

  const appConfigs = apiConfig?.configs || [];

  return (
    <Box
      sx={{
        flex: 1,
        flexDirection: "column",
        display: "flex",
        overflow: "hidden",
      }}
    >
      <PageHeader
        title="Personas"
        subtitle="Manage AI personas for your application tests"
        actions={
          <Tooltip title="New Persona" placement="top">
            <IconButton
              size="small"
              onClick={handleCreate}
              disabled={isLoadingConfig}
              sx={{
                borderRadius: 2,
                p: 1.5,
                border: "0px solid",
                borderColor: "grey.300",
                color: "grey.800",
                backgroundColor: "transparent",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "background.default",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      >
        <Stack direction="row" spacing={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <SearchBar
              size="small"
              value={searchQuery}
              onChange={setSearchQuery}
              label="Search personas..."
            />
          </Box>
          <FormControl size="small" fullWidth sx={{ maxWidth: 200 }}>
            <InputLabel>Application</InputLabel>
            <Select
              value={selectedAppConfig}
              onChange={(e) => setSelectedAppConfig(e.target.value)}
              label="Application"
              defaultValue="#"
            >
              <MenuItem value="#">All Apps</MenuItem>
              {appConfigs.map((config) => (
                <MenuItem key={config.id} value={config.id}>
                  {config.base_url}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </PageHeader>

      {/* Content */}
      {isLoadingPersonas ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : filteredPersonas.length === 0 && !searchQuery ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <PersonaCard
                persona={samplePersona}
                onEdit={() => {}}
                onDelete={() => {}}
                onView={() => {}}
                onCopy={() => {}}
                disabled
                example
              />
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredPersonas.map((persona) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={persona.id}>
              <PersonaCard
                persona={persona}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onCopy={handleCopy}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <PersonaDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        persona={editPersona || undefined}
        appConfigs={appConfigs}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
        isEdit={!!editPersona}
      />

      {/* View Dialog */}
      <Dialog
        open={!!viewPersona}
        onClose={() => setViewPersona(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        {viewPersona && (
          <>
            <DialogTitle
              sx={{
                pb: 2,
                borderBottom: 1,
                borderColor: "grey.200",
                backgroundColor: "background.default",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 48,
                    height: 48,
                  }}
                >
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {viewPersona.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    AI Persona Details
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "grey.800",
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.5,
                    }}
                  >
                    {viewPersona.description || "No description provided"}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "grey.800",
                      mb: 1,
                    }}
                  >
                    App Configuration
                  </Typography>
                  <Chip
                    label={viewPersona.app_config?.name || "None"}
                    sx={{
                      backgroundColor: STATUS_COLORS.info.bg,
                      color: STATUS_COLORS.info.text,
                      fontWeight: 500,
                    }}
                  />
                </Box>

                {(viewPersona.user || viewPersona.password) && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "grey.800",
                        mb: 1,
                      }}
                    >
                      Credentials
                    </Typography>
                    <Stack spacing={1}>
                      {viewPersona.user && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Username:</strong> {viewPersona.user}
                        </Typography>
                      )}
                      {viewPersona.password && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Password:</strong> ••••••••
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "grey.800",
                      mb: 1,
                    }}
                  >
                    System Prompt
                  </Typography>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: "background.default",
                      border: 1,
                      borderColor: "grey.200",
                      borderRadius: 2,
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                      color: "grey.800",
                    }}
                  >
                    {viewPersona.system_prompt}
                  </Paper>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: "grey.800",
                      mb: 1,
                    }}
                  >
                    Details
                  </Typography>
                  <List
                    dense
                    sx={{
                      backgroundColor: "background.default",
                      borderRadius: 2,
                      border: 1,
                      borderColor: "grey.200",
                    }}
                  >
                    <ListItem>
                      <ListItemText
                        primary="Created"
                        secondary={new Date(viewPersona.created_at).toLocaleString()}
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, color: "grey.800" },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: "text.secondary" },
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Last Updated"
                        secondary={
                          viewPersona.updated_at
                            ? new Date(viewPersona.updated_at).toLocaleString()
                            : "Never"
                        }
                        primaryTypographyProps={{
                          sx: { fontWeight: 500, color: "grey.800" },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: "text.secondary" },
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions
              sx={{
                p: 3,
                pt: 2,
                borderTop: 1,
                borderColor: "grey.200",
                backgroundColor: "background.default",
              }}
            >
              <Button
                onClick={() => setViewPersona(null)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "grey.200",
                  },
                }}
              >
                Close
              </Button>
              <Button
                variant="gradient"
                onClick={() => {
                  setViewPersona(null);
                  handleEdit(viewPersona);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Edit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add persona"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: { xs: "flex", md: "none" },
          background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
            boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
          },
        }}
        onClick={handleCreate}
        disabled={isLoadingConfig}
      >
        <AddIcon />
      </Fab>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={!!deletePersona}
        onClose={() => setDeletePersona(null)}
        onConfirm={handleConfirmDelete}
        itemName={deletePersona?.name || ""}
        itemType="persona"
        isDeleting={isDeleting}
      />
    </Box>
  );
}
